import { CobaltButton } from '@cae/cobalt-react/components/button';
import {
  CobaltTable,
  CobaltTableBody,
  CobaltTableCell,
  CobaltTableContainer,
  CobaltTableHead,
  CobaltTableRow,
} from '@cae/cobalt-react/components/table';
import { CobaltIconDownloadOutline } from '@cae/cobalt-react/icons/outline/CobaltIconDownloadOutline';
import { CobaltIconFileSolid } from '@cae/cobalt-react/icons/solid/CobaltIconFileSolid';
import { CobaltIconFolderSolid } from '@cae/cobalt-react/icons/solid/CobaltIconFolderSolid';
import { useDocumentsFilter } from '@features/documents/contexts/documents-filter/useDocumentsFilter';
import { useDownloadDocument } from '@features/documents/hooks/use-download-document/useDownloadDocument';
import * as React from 'react';

import type { DocumentsTableProps } from './DocumentsTable.types';

import styles from './DocumentsTable.module.css';
import { buildParentFoldersForNavigation } from './DocumentsTable.utils';

const DocumentsTable = React.memo(
  ({
    documents,
    organizationId,
    canDownload,
  }: Readonly<DocumentsTableProps>): React.ReactElement => {
    const { filters, navigateToFolder } = useDocumentsFilter();
    const { download, isDocumentDownloading } = useDownloadDocument();

    const handleFolderClick = React.useCallback(
      (documentId: string): void => {
        const doc = documents.find(d => d.id === documentId);
        if (!doc) return;
        const newParentFolders = buildParentFoldersForNavigation(
          doc,
          filters.parentFolders
        );
        navigateToFolder(documentId, newParentFolders);
      },
      [documents, filters.parentFolders, navigateToFolder]
    );

    const handleDownload = React.useCallback(
      (documentId: string): void => {
        download(documentId, organizationId);
      },
      [download, organizationId]
    );

    return (
      <CobaltTableContainer
        className={styles.documentsTable}
        data-testid="documents-table"
      >
        <CobaltTable size="x-large">
          <CobaltTableHead>
            <CobaltTableRow>
              <CobaltTableCell header>Name</CobaltTableCell>
              <CobaltTableCell header className={styles.downloadCell} />
            </CobaltTableRow>
          </CobaltTableHead>
          <CobaltTableBody>
            {documents.map(doc => (
              <CobaltTableRow
                key={doc.id}
                className={!doc.isFile ? styles.folderRow : undefined}
                onClick={
                  !doc.isFile ? () => handleFolderClick(doc.id) : undefined
                }
                data-testid={`document-row-${doc.id}`}
                interactive
              >
                <CobaltTableCell className={styles.nameCell}>
                  <span className={`${styles.nameCell} body-02`}>
                    {doc.isFile ? (
                      <CobaltIconFileSolid
                        size="medium"
                        className={styles.fileIcon}
                      />
                    ) : (
                      <CobaltIconFolderSolid
                        size="medium"
                        className={styles.folderIcon}
                      />
                    )}
                    {doc.name}
                  </span>
                </CobaltTableCell>
                <CobaltTableCell className={styles.downloadCell}>
                  {doc.isFile && canDownload && (
                    <CobaltButton
                      variant="ghost"
                      size="small"
                      aria-label={`Download ${doc.name}`}
                      onClick={() => handleDownload(doc.id)}
                      disabled={isDocumentDownloading(doc.id)}
                      pending={isDocumentDownloading(doc.id)}
                    >
                      <CobaltIconDownloadOutline />
                    </CobaltButton>
                  )}
                </CobaltTableCell>
              </CobaltTableRow>
            ))}
          </CobaltTableBody>
        </CobaltTable>
      </CobaltTableContainer>
    );
  }
);

DocumentsTable.displayName = 'DocumentsTable';

export { DocumentsTable };
